import { useEffect, useState } from 'react';
import type { AdminEmailTemplateRow } from '../../../domain/superadmin/types';
import {
    TEST_FROM_ALLOWLIST,
    type TestFromAddress,
} from '../../../domain/superadmin/emailTest';
import { AppIcon } from '../../../components/icons/AppIcon';
import { Button, Input, Select } from '../../../components/ui';
import Modal from '../../../components/ui/Modal';
import { sendAdminTestEmail } from '../../../services/modules/superadmin/superadminApi';
import { SuperadminApiError } from '../../../services/modules/superadmin/errors';
import { notify } from '../../../utils/toast';

interface EmailTemplateTestSendModalProps {
    template: AdminEmailTemplateRow | null;
    isOpen: boolean;
    onClose: () => void;
}

const fromOptions = TEST_FROM_ALLOWLIST.map((email) => ({
    value: email,
    label: email,
}));

export function EmailTemplateTestSendModal({
    template,
    isOpen,
    onClose,
}: EmailTemplateTestSendModalProps) {
    const [to, setTo] = useState('');
    const [from, setFrom] = useState<TestFromAddress>(TEST_FROM_ALLOWLIST[0]);
    const [isSending, setIsSending] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setTo('');
        setFrom(TEST_FROM_ALLOWLIST[0]);
    }, [isOpen, template?.key]);

    if (!template) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const recipient = to.trim();
        if (!recipient) return;

        setIsSending(true);
        try {
            await sendAdminTestEmail({ key: template.key, to: recipient, from });
            notify.success('E-mail de teste enviado com sucesso.');
            onClose();
        } catch (err) {
            if (err instanceof SuperadminApiError && err.isForbidden) {
                notify.warning('Acesso não autorizado à área de superadmin.');
                return;
            }
            notify.error(err instanceof Error ? err.message : 'Falha ao enviar e-mail de teste.');
        } finally {
            setIsSending(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" preventBackdropClose={isSending}>
            <Modal.Header onClose={onClose}>Enviar e-mail de teste</Modal.Header>
            <Modal.Body>
                <p className="text-sm text-secondary">
                    Envia o template <strong className="text-default">{template.name}</strong> com dados
                    de exemplo.
                </p>
                <form id="email-test-send-form" onSubmit={handleSubmit} className="space-y-4">
                    <Input
                        label="Destinatário"
                        name="to"
                        type="email"
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        placeholder="email@exemplo.com"
                        required
                        disabled={isSending}
                    />
                    <Select
                        label="Remetente"
                        name="from"
                        options={fromOptions}
                        value={from}
                        onChange={(e) => setFrom(e.target.value as TestFromAddress)}
                        disabled={isSending}
                    />
                </form>
            </Modal.Body>
            <Modal.Footer className="flex-col-reverse gap-2 sm:flex-row">
                <Button
                    type="button"
                    variant="secondary"
                    onClick={onClose}
                    className="w-full sm:w-auto"
                    disabled={isSending}
                >
                    Cancelar
                </Button>
                <Button
                    type="submit"
                    form="email-test-send-form"
                    className="w-full sm:w-auto"
                    disabled={isSending || !to.trim()}
                >
                    <AppIcon name="send" className="h-4 w-4" />
                    {isSending ? 'Enviando...' : 'Enviar'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
